// MODELS FOUND BY WHAT THEY ARE ABOUT: their country, client, brand, category and sub-category.
//
// The curated values live in `client_metadata`; older models only carry them in their reference
// config (the brands and their countries, the category) or on `custom_model`, and are read from
// there when the curated field is empty — the same order Manage Models labels them in.
//
// And the faceting itself, shared by the model importer's two lists: a row carries `facets`, one
// array of values per key, and a search string. OR inside a key, AND across keys; a row matches a
// key's choice when ANY of its values was chosen, which is what lets a model with two brands be
// found under either.
//
// Pure functions only.

export const MODEL_FACET_KEYS = ['country', 'client', 'brand', 'category', 'subcategory']
// The value a row holds at a key nobody filled in. A sentinel rather than null so it can be a chip
// like any other value, and one no real value can collide with.
export const UNCLASSIFIED = '__NG_UNCLASSIFIED__'

function clean (value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function unique (values) {
  const seen = {}
  return values.map(clean).filter(value => {
    if (value.length === 0 || seen[value]) return false
    seen[value] = true
    return true
  })
}

function firstFilled () {
  for (let i = 0; i < arguments.length; i++) {
    const values = unique(arguments[i])
    if (values.length > 0) return values
  }
  return []
}

// { country: [...], client: [...], brand: [...], category: [...], subcategory: [...] }
//
// A country is stored as a registry code ("CO") in older reference configs and as a name
// ("Colombia") in client_metadata. `countryKey`, when given, folds both into one value — the
// registry code — so the same market is never two different countries.
export function modelMetadata (model, countryKey) {
  const meta = (model && model.client_metadata) || {}
  const config = (model && model.reference_model && model.reference_model.config) || {}
  const custom = (model && model.custom_model) || {}
  const brands = config.brands && typeof config.brands === 'object' ? config.brands : {}
  const brandCountries = Object.keys(brands).map(name => brands[name] && brands[name].country)
  const country = firstFilled([meta.country], brandCountries.slice(0, 1), [custom.country])
  return {
    country: countryKey ? unique(country.map(countryKey)) : country,
    client: unique([meta.client]),
    brand: firstFilled([meta.brand], Object.keys(brands), [custom.brand]),
    category: firstFilled([meta.category], [config.category]),
    subcategory: unique([meta['sub-category']])
  }
}

// One row per model, for the importer's model list.
export function modelRows (models, labelFor, countryKey) {
  return (models || []).filter(model => model && model.id).map(model => {
    const facets = modelMetadata(model, countryKey)
    const name = String(model.name || model.id)
    return {
      id: model.id,
      modelId: model.id,
      name: name,
      facets: facets,
      search: searchText([name], facets, labelFor)
    }
  })
}

// Everything a search term may be found in, lower-cased and space-joined: a term never holds a
// space, so none can match across two fields. Facet values are searched as they are SHOWN (a
// country by its name, not its code) and as they are stored.
export function searchText (words, facets, labelFor) {
  const parts = words.slice()
  Object.keys(facets || {}).forEach(key => {
    facets[key].forEach(value => {
      parts.push(value)
      const shown = labelFor ? labelFor(key, value) : null
      if (shown && shown !== value) parts.push(shown)
    })
  })
  return parts.join(' ').toLowerCase()
}

export function facetValues (row, key) {
  const values = row.facets[key]
  return values && values.length > 0 ? values : [UNCLASSIFIED]
}

function queryTerms (query) {
  return String(query || '').toLowerCase().split(/\s+/).filter(term => term.length > 0)
}

function matchesTerms (row, terms) {
  return terms.every(term => row.search.indexOf(term) !== -1)
}

// `skipKey` leaves one key's own choice out, which is how that key's counts are taken.
function matchesSelection (row, selection, keys, skipKey) {
  return keys.every(key => {
    if (key === skipKey) return true
    const chosen = selection[key]
    if (!chosen || chosen.length === 0) return true
    return facetValues(row, key).some(value => chosen.indexOf(value) !== -1)
  })
}

export function filterRows (rows, selection, query, keys) {
  const terms = queryTerms(query)
  const chosen = selection || {}
  return rows.filter(row => matchesTerms(row, terms) && matchesSelection(row, chosen, keys, null))
}

// The chips of every key, each counted under the search and under every OTHER key's choice: the
// number on a chip is how many rows the list would hold with that chip added to what is already
// chosen at its key.
//
// A key no row anywhere carries a value for is marked hidden: a group holding one Unclassified chip
// is a question with a single answer. A chosen value stays on the list at zero, so it can always be
// taken off where it was put on.
export function facetCounts (rows, selection, query, keys) {
  const terms = queryTerms(query)
  const chosen = selection || {}
  const searched = rows.filter(row => matchesTerms(row, terms))
  return keys.map(key => {
    const counts = {}
    searched.forEach(row => {
      if (!matchesSelection(row, chosen, keys, key)) return
      facetValues(row, key).forEach(value => {
        counts[value] = (counts[value] || 0) + 1
      })
    })
    ;(chosen[key] || []).forEach(value => {
      if (!counts[value]) counts[value] = 0
    })
    const values = Object.keys(counts)
      .map(value => ({ value: value, count: counts[value] }))
      .sort((a, b) => {
        if (a.value === UNCLASSIFIED) return 1
        if (b.value === UNCLASSIFIED) return -1
        return (b.count - a.count) || a.value.localeCompare(b.value)
      })
    return {
      key: key,
      visible: rows.some(row => row.facets[key] && row.facets[key].length > 0),
      values: values
    }
  })
}
